import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {HttpMethod} from '@/constants/httpMethod';
import {EReducersPath} from '@/dataSource/utils/ReducerPaths';

import {setWafHeaders} from '../headers';
import {reAuth} from '../reAuth';

import {apiBasePaymentLink} from './constants';
import {
  IFeesResponse,
  IGetCheckoutResultResponse,
  IPaymentLinkResponse,
  IPublicRequestCheckoutUrl,
  IResponseCheckoutUrl,
} from './entities';

const baseQuery = async (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    timeout: 30 * 1000,
    prepareHeaders: async headers => {
      await setWafHeaders(baseUrl, headers);
      return headers;
    },
  });

const baseApiQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = apiBasePaymentLink.baseUrl;

  const result = await reAuth(args, api, extraOptions, baseUrl, baseQuery);
  return result;
};

export const paymentLinkApi = createApi({
  reducerPath: EReducersPath.PAYMENT_LINK_API,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  baseQuery: baseApiQueryWithReAuth,
  endpoints: build => ({
    getPaymentLink: build.query<IPaymentLinkResponse, string>({
      query: (id: string) =>
        `${apiBasePaymentLink.endpoints.getPaymentLink}/${id}`,
    }),
    getFees: build.query<IFeesResponse, void>({
      query: () => apiBasePaymentLink.endpoints.getFees,
    }),
    getCheckoutResult: build.query<IGetCheckoutResultResponse, string>({
      query: (id: string) =>
        `${apiBasePaymentLink.endpoints.getCheckoutResult}/${id}`,
    }),
    postLinkCheckout: build.mutation<
      IResponseCheckoutUrl,
      IPublicRequestCheckoutUrl
    >({
      query: (body: IPublicRequestCheckoutUrl) => ({
        url: apiBasePaymentLink.endpoints.getCheckoutLink,
        method: HttpMethod.POST,
        body,
      }),
    }),
  }),
});

export const {
  useLazyGetPaymentLinkQuery,
  useLazyGetFeesQuery,
  usePostLinkCheckoutMutation,
  useLazyGetCheckoutResultQuery,
} = paymentLinkApi;
